import axios from 'axios';
import { ISaveAttachmentDTO } from '../dtos/save-attachment-dto';
import { apolloClient } from '@/apollo';
import { AttachmentQueries } from '@/api/attachments/graphql/attachment.queries';
import { AttachmentMutations } from '@/api/attachments/graphql/attachment.mutations';
import { IDocumentAttachment } from '@lib-shared/types/document-attachment';
import { EFetchPolicy } from '@/api/types/enums/fetch-policy';

export class AttachmentService {
  static async setRequireView(id: string, requireView: boolean): Promise<boolean> {
    const { data } = await apolloClient.mutate({
      mutation: AttachmentMutations.updateDocumentAttachmentMutation,
      variables: {
        id,
        requireView,
      },
    });
    return !!data;
  }

  static async setRestrictedAccess(id: string, recipientIds: string[]): Promise<boolean> {
    const { data } = await apolloClient.mutate({
      mutation: AttachmentMutations.updateDocumentAttachmentMutation,
      variables: {
        id,
        accessRecipientList: recipientIds,
      },
    });
    return !!data;
  }

  static async removeRestrictedAccess(id: string): Promise<boolean> {
    const { data } = await apolloClient.mutate({
      mutation: AttachmentMutations.updateDocumentAttachmentMutation,
      variables: {
        id,
        accessRecipientList: null,
      },
    });
    return !!data;
  }

  static async delete(id: string): Promise<boolean> {
    const { data } = await apolloClient.mutate({
      mutation: AttachmentMutations.removeDocumentAttachmentMutation,
      variables: {
        id,
      },
    });
    return data;
  }

  static async load(documentId: string): Promise<IDocumentAttachment[]> {
    const {
      data: { document },
    } = await apolloClient.query({
      query: AttachmentQueries.documentAttachments,
      variables: { documentId },
      fetchPolicy: EFetchPolicy.NetworkOnly,
    });
    return document.documentAttachments;
  }

  static async save(payload: ISaveAttachmentDTO): Promise<string> {
    const body: FormData = new FormData();
    body.append('document-id', payload.documentId);
    body.append('attachment-id', payload.id);
    body.append('attachment-title', payload.title);
    body.append('attachment-description', payload.description);
    body.append('require-view', payload.requireView ? '1' : '0');
    body.append('restrict-access', payload.restrictAccess ? '1' : '0');
    body.append('attachment-type', payload.type);

    const { data } = await axios.post('/save/attachment', body);

    if (data.error) {
      throw new Error(data.error);
    }
    if (data.status !== 1) {
      throw new Error(data);
    }
    return data.id;
  }

  static async setRestrictAccessLegacy(
    documentId: string,
    attachmentId: string,
    recipientIds: string[]
  ): Promise<void> {
    const body = new FormData();
    body.append('document-id', documentId);
    body.append('attachment-id', attachmentId);
    recipientIds.forEach((id: string) => {
      body.append('access-recipient-list[]', id);
    });

    const { data } = await axios.post('/save/attachment', body);

    if (data.error) {
      throw new Error(data.error);
    }
    if (data.status !== 1) {
      throw new Error(data.error);
    }
    return data;
  }
}
